import React, { useState, useEffect } from "react";
import { PromotionsService } from "../../../Servicefile/PromotionsService"; // ⬅️ adjust this path to match your project structure

const ORANGE = "#f97316";
const cardShadow = "0 20px 60px rgba(15,23,42,0.25)";

export default function AddNewModal({
    show,
    onClose,
    onSubmit,
    isEditMode = false,
    selectedOverview = null
}) {
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        type: "OVERVIEW"
    });
    const [imagePreviews, setImagePreviews] = useState([]); // ✅ array now
    const [imageFiles, setImageFiles] = useState([]);       // ✅ array now
    const [extraFields, setExtraFields] = useState([]);
    const [submitting, setSubmitting] = useState(false);    // ⬅️ NEW
    const [submitError, setSubmitError] = useState("");     // ⬅️ NEW

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // ✅ handles multiple files at once, and appends to existing selection
    const handleImageChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setImageFiles((prev) => [...prev, ...files]);
        setImagePreviews((prev) => [
            ...prev,
            ...files.map((file) => URL.createObjectURL(file)),
        ]);

        // allow re-selecting the same file(s) again later
        e.target.value = "";
    };

    const removeImage = (index) => {
        setImagePreviews((prev) => prev.filter((_, i) => i !== index));
        setImageFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleAddField = () => {
        setExtraFields((prev) => [
            ...prev,
            { id: Date.now(), value: "" },
        ]);
    };

    const handleExtraFieldChange = (id, value) => {
        setExtraFields((prev) =>
            prev.map((f) => (f.id === id ? { ...f, value } : f))
        );
    };

    const handleRemoveField = (id) => {
        setExtraFields((prev) => prev.filter((f) => f.id !== id));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setSubmitError("");
        setSubmitting(true);

        // Same payload shape is used for both create and update now.
        const overviewPayload = {
            title: formData.title.trim(),
            description: formData.description.trim(),
            type: formData.type,
            featureDescription: extraFields
                .map((f) => f.value.trim())
                .filter(Boolean),
        };

        //console.log("Overview payload:", overviewPayload);
        //console.log("Image files:", imageFiles);

        try {
            let resultData;

            if (isEditMode && selectedOverview) {
                // UPDATE — updateOverview already returns response.data
                resultData = await PromotionsService.updateOverview(
                    selectedOverview.id,
                    overviewPayload,
                    imageFiles
                );
            } else {
                // CREATE — createOverview returns the full axios response
                const response = await PromotionsService.createOverview(
                    overviewPayload,
                    imageFiles
                );
                resultData = response?.data;
            }

          

            onSubmit?.(resultData ?? overviewPayload);

            // Reset form
            setFormData({ title: "", description: "", type: "OVERVIEW" });
            setExtraFields([]);
            setImageFiles([]);
            setImagePreviews([]);

            onClose?.();

        } catch (error) {
            console.error(
                isEditMode ? "Failed to update overview:" : "Failed to create overview:",
                error.response?.data || error
            );

            setSubmitError(
                error.response?.data?.message ||
                error.response?.data?.error ||
                "Something went wrong while saving this. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };


    useEffect(() => {
        if (!show) return;

        if (isEditMode && selectedOverview) {

            //console.log("Loading selected overview into modal:", selectedOverview);

            // ============================
            // TITLE + DESCRIPTION
            // ============================

            setFormData({
                title: selectedOverview.title || "",
                description: selectedOverview.description || "",
                type: selectedOverview.type || "OVERVIEW"
            });


            // ============================
            // FEATURES
            // ============================

            const existingFeatures =
                Array.isArray(selectedOverview.featureDescription)
                    ? selectedOverview.featureDescription
                    : [];

            setExtraFields(
                existingFeatures.map((feature, index) => ({
                    id: Date.now() + index,
                    value: feature || "",
                }))
            );


            // ============================
            // EXISTING IMAGES
            // ============================

            const existingImages =
                Array.isArray(selectedOverview.imageUrls)
                    ? selectedOverview.imageUrls
                    : [];

            setImagePreviews(existingImages);

            // Important:
            // Existing images are URLs, not File objects.
            // Therefore don't put them inside imageFiles.
            setImageFiles([]);

        } else {

            // ============================
            // ADD MODE
            // ============================

            setFormData({
                title: "",
                description: "",
                type: "OVERVIEW"
            });

            setExtraFields([]);

            setImageFiles([]);

            setImagePreviews([]);
        }

        setSubmitError("");

    }, [show, isEditMode, selectedOverview]);


    if (!show) return null;

    return (
        <div
            onClick={onClose}
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15,23,42,0.55)",
                backdropFilter: "blur(2px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                padding: "20px",
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: "#fff",
                    borderRadius: 18,
                    padding: "26px 28px",
                    width: "100%",
                    maxWidth: 440,
                    maxHeight: "88vh",
                    overflowY: "auto",
                    boxShadow: cardShadow,
                    scrollbarWidth: "none",
                }}
            >
                {/* Header */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 20,
                    }}
                >
                    <h3 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: "#0f172a" }}>
                        Add New
                    </h3>
                    <button
                        onClick={onClose}
                        style={{
                            border: "none",
                            background: "#f3f4f6",
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            fontSize: 16,
                            lineHeight: 1,
                            cursor: "pointer",
                            color: "#6b7280",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#e5e7eb")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "#f3f4f6")}
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {/* Title */}
                    <div>
                        <label style={labelStyle}>Title</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleFormChange}
                            required
                            placeholder="Enter title"
                            style={inputStyle}
                            onFocus={(e) => (e.target.style.borderColor = ORANGE)}
                            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                        />
                    </div>
                    {/* Type */}
                    <div>
                        <label style={labelStyle}>Type</label>

                        <select
                            name="type"
                            value={formData.type}
                            onChange={handleFormChange}
                            required
                            style={{ ...inputStyle, background: "#fff", cursor: "pointer" }}
                            onFocus={(e) => (e.target.style.borderColor = ORANGE)}
                            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                        >
                            <option value="OVERVIEW">OVERVIEW</option>
                            <option value="BRANDING">BRANDING</option>
                            <option value="LEADS">LEADS</option>
                            <option value="OFFERS">OFFERS</option>
                            <option value="DIGITAL">DIGITAL</option>
                        </select>
                    </div>
                    {/* Description */}
                    <div>
                        <label style={labelStyle}>Description</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleFormChange}
                            rows={3}
                            placeholder="Enter description"
                            style={{ ...inputStyle, resize: "vertical" }}
                            onFocus={(e) => (e.target.style.borderColor = ORANGE)}
                            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                        />
                    </div>

                    {/* Dynamic extra fields (Features -> featureDescription[]) */}
                    {extraFields.length > 0 && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {extraFields.map((field, idx) => (
                                <div key={field.id} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                    <input
                                        type="text"
                                        value={field.value}
                                        onChange={(e) => handleExtraFieldChange(field.id, e.target.value)}
                                        placeholder={`Feature ${idx + 1}`}
                                        style={{ ...inputStyle, flex: 1 }}
                                        onFocus={(e) => (e.target.style.borderColor = ORANGE)}
                                        onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveField(field.id)}
                                        style={{
                                            border: "none",
                                            background: "#fee2e2",
                                            color: "#b91c1c",
                                            width: 30,
                                            height: 30,
                                            borderRadius: 8,
                                            cursor: "pointer",
                                            fontSize: 14,
                                            flexShrink: 0,
                                        }}
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Add Field button */}
                    <button
                        type="button"
                        onClick={handleAddField}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            padding: "9px",
                            borderRadius: 10,
                            border: `1.5px dashed ${ORANGE}`,
                            background: "#fff7ed",
                            color: ORANGE,
                            fontWeight: 700,
                            fontSize: 13,
                            cursor: "pointer",
                            transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#ffedd5")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "#fff7ed")}
                    >
                        + Add Field
                    </button>

                    {/* Image Upload — multi-image support */}
                    <div>
                        <label style={labelStyle}>
                            Images {imagePreviews.length > 0 && `(${imagePreviews.length})`}
                        </label>

                        {/* Previews grid — shown whenever at least one image is selected */}
                        {imagePreviews.length > 0 && (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))",
                                    gap: 10,
                                    marginBottom: 10,
                                }}
                            >
                                {imagePreviews.map((src, index) => (
                                    <div key={index} style={{ position: "relative" }}>
                                        <img
                                            src={src}
                                            alt={`Preview ${index + 1}`}
                                            style={{
                                                width: "100%",
                                                height: 90,
                                                objectFit: "cover",
                                                borderRadius: 10,
                                                border: "1px solid #e5e7eb",
                                                display: "block",
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeImage(index)}
                                            style={{
                                                position: "absolute",
                                                top: 4,
                                                right: 4,
                                                border: "none",
                                                background: "rgba(15,23,42,0.7)",
                                                color: "#fff",
                                                width: 22,
                                                height: 22,
                                                borderRadius: "50%",
                                                fontSize: 12,
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Dropzone — always visible, so more images can be added anytime */}
                        <label
                            htmlFor="add-new-image-input"
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 6,
                                padding: imagePreviews.length > 0 ? "16px" : "28px 16px",
                                borderRadius: 12,
                                border: "2px dashed #d1d5db",
                                background: "#fafafa",
                                cursor: "pointer",
                                transition: "border-color 0.15s, background 0.15s",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = ORANGE;
                                e.currentTarget.style.background = "#fff7ed";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = "#d1d5db";
                                e.currentTarget.style.background = "#fafafa";
                            }}
                        >
                            <div
                                style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    background: "#fff3ec",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 18,
                                }}
                            >
                                🖼️
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                                {imagePreviews.length > 0
                                    ? "Click to add more images"
                                    : "Click to upload images"}
                            </span>
                            <span style={{ fontSize: 11, color: "#9ca3af" }}>
                                PNG, JPG up to 5MB each · multiple allowed
                            </span>
                            <input
                                id="add-new-image-input"
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleImageChange}
                                style={{ display: "none" }}
                            />
                        </label>
                    </div>

                    {/* Error message */}
                    {submitError && (
                        <div
                            style={{
                                background: "#fef2f2",
                                border: "1px solid #fecaca",
                                color: "#b91c1c",
                                fontSize: 12.5,
                                fontWeight: 600,
                                padding: "10px 12px",
                                borderRadius: 9,
                            }}
                        >
                            {submitError}
                        </div>
                    )}

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={submitting}
                        style={{
                            padding: "12px",
                            borderRadius: 10,
                            border: "none",
                            background: submitting ? "#fdba74" : ORANGE,
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 14,
                            cursor: submitting ? "not-allowed" : "pointer",
                            marginTop: 4,
                            transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => {
                            if (!submitting) e.currentTarget.style.background = "#ea580c";
                        }}
                        onMouseLeave={(e) => {
                            if (!submitting) e.currentTarget.style.background = ORANGE;
                        }}
                    >
                        {submitting ? "Submitting..." : "Submit"}
                    </button>
                </form>
            </div>
        </div>
    );
}

const labelStyle = {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 6,
};

const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 9,
    border: "1px solid #d1d5db",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    transition: "border-color 0.15s",
};